import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, Zap, Home, Building, CheckCircle } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import biomas from './images1/Biomass.png';
const comparisonData = [
  {
    parameter: 'Process Type',
    bioguest: 'In-vessel aerobic, natural',
    automatic: 'Thermally accelerated',
    semiAuto: 'Hybrid (machine + yard)',
    home: 'Natural aerobic'
  },
  {
    parameter: 'Heating Element',
    bioguest: 'Not required',
    automatic: 'Required',
    semiAuto: 'Not required',
    home: 'Not required'
  },
  {
    parameter: 'Energy Consumption',
    bioguest: 'Very low',
    automatic: 'High',
    semiAuto: 'Low',
    home: 'Nil'
  },
  {
    parameter: 'Processing Time',
    bioguest: 'Continuous, biological',
    automatic: '~24 hours',
    semiAuto: '3-4 weeks',
    home: 'Gradual'
  },
  {
    parameter: 'Odor Control',
    bioguest: 'Excellent (in-vessel)',
    automatic: 'Moderate',
    semiAuto: 'Depends on management',
    home: 'Excellent'
  },
  {
    parameter: 'Compost Quality',
    bioguest: 'High, stabilized',
    automatic: 'Partially stabilized',
    semiAuto: 'High',
    home: 'High'
  },
  {
    parameter: 'Space Requirement',
    bioguest: 'Low',
    automatic: 'Low',
    semiAuto: 'High (yard needed)',
    home: 'Very low'
  },
  {
    parameter: 'Environmental Impact',
    bioguest: 'Highly sustainable',
    automatic: 'Energy intensive',
    semiAuto: 'Sustainable',
    home: 'Highly sustainable'
  },
  {
    parameter: 'Compliance (SWM 2016)',
    bioguest: 'Yes',
    automatic: 'Conditional',
    semiAuto: 'Yes',
    home: 'Yes'
  }
];

const systems = [
  {
    title: 'BIOGUEST-V',
    subtitle: 'Fully Automatic Natural In-Vessel Composting',
    icon: Building,
    features: [
      'No thermal heaters required',
      'Integrated curing mechanism',
      'Very low power consumption',
      'Odor-free operation',
      'Capacity: 25 kg to 2000 kg/day'
    ],
    description: 'Relies on optimized microbial kinetics, controlled aeration, and internal curing to convert organic waste into compost with minimal energy input.'
  },
  {
    title: '24-Hour Automatic Converter',
    subtitle: 'Thermal Accelerated System',
    icon: Zap,
    features: [
      'Continuous automated operation',
      'High throughput capacity',
      'Rapid processing',
      'Capacity: 100 kg to 5000 kg/day',
      'Accelerated moisture reduction'
    ],
    description: 'Mechanized, thermally accelerated waste processing unit designed to rapidly convert organic waste within a short cycle time.'
  },
  {
    title: 'Semi-Automatic System',
    subtitle: 'Hybrid Composting Solution',
    icon: Leaf,
    features: [
      'Initial mechanical mixing (~2 hours)',
      'Secondary aerobic curing (3-4 weeks)',
      'Lower energy costs',
      'Capacity: 100 kg to 5000 kg/day',
      'Produces stable compost'
    ],
    description: 'Combines short-duration mechanical initiation with yard-based aerobic maturation for optimal composting results.'
  },
  {
    title: 'Home Composting',
    subtitle: 'Non-Electric Solution',
    icon: Home,
    features: [
      'Zero electricity consumption',
      'Compact and aesthetic design',
      'Daily waste loading',
      'Low maintenance',
      'Perfect for households'
    ],
    description: 'Non-electric, compact, in-vessel composting solution intended for households and small establishments.'
  }
];

export const Biomass = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="composting-page">
      {/* Hero Section */}
      <section className="bg-emerald-950 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-amber-400 uppercase tracking-[0.3em] text-sm font-semibold">
              Briquettes & Pellets
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
               Biomass Briquettes & Pellets
            </h1>
            {/* <p className="text-white/70 text-lg max-w-3xl mx-auto">
              Digestate is the nutrient-rich residual material generated from the anaerobic digestion (AD) process.
            </p> */}
          </motion.div>
        </div>
      </section>

      {/* Organic Waste Composting Systems (OWC) */}
      <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
               Technical Consulting & Project Support
              </h2>
              <p className="text-slate-600 dark:text-white/70 mb-6 tracking-wider">
                Biomass briquettes and pellets are produced from renewable organic residues such as agricultural by-products, forestry waste, and wood-based biomass. These fuels offer high calorific value, consistent combustion characteristics, and reduced emissions, making them suitable for a wide range of thermal and energy applications.
              </p>
              <p className="text-slate-600 dark:text-white/70 mb-6 tracking-wider">RENVITEK offers specialized consulting and engineering services for the planning, optimization, and implementation of biomass briquette and pellet-based fuel systems,enabling a sustainable transition away from conventional fossil fuels. Our solutions support industrial and institutional users in adopting renewable, low-emission thermal energy systems.</p>
              <p className="text-slate-600 dark:text-white/70 mb-6 tracking-wider">Backed by strong process engineering capabilities and hands-on project experience, RENVITEK delivers technically robust biomass energy solutions focused on performance, safety, and lifecycle cost effectiveness. Our approach ensures seamless fuel integration, stable operation, and long-term economic sustainability for each application.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={biomas}
                alt="Composting System"
                className="w-full h-auto rounded-lg shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

{/* Digestate Analysis & Quality Assessment */}
      <section className="py-12 md:py-10 bg-slate-50 dark:bg-emerald-900/30">
        <div className="w-full px-4 sm:px-6 lg:px-12">
        <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6 text-center">Scope of RENVITEK’s Consulting Services</h2>
          
          <div className="mb-8">
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">RENVITEK supports clients across the entire value chain of biomass fuel utilization, including:</p>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
              <li>Feedstock assessment and selection (agricultural residues, sawdust, biomass waste)</li>
              <li>Technology selection for briquetting and pelletizing systems</li>
              <li>Plant capacity planning and layout engineering</li>
              <li>Fuel quality optimization (moisture control, density, calorific value)</li>
              <li>Integration with existing boilers, furnaces, and thermal systems</li>
              <li>Economic feasibility analysis and lifecycle cost evaluation</li>
              <li>Emission reduction and sustainability assessment</li>
            </ul></p><br></br>
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
                Technical Advantages of Biomass-Based Fuels
              </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
              <li>Renewable and carbon-neutral energy source</li>
              <li>Uniform size and controlled combustion behavior</li>
              <li>Reduced ash and sulfur emissions compared to coal</li>
              <li>Lower operating costs and fuel price volatility</li>
              <li>Supports waste-to-energy and circular economy principles</li>
            </ul></p>
            </div>
        </motion.div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-12 md:py-10 bg-amber-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-emerald-950 mb-4">
            Ready to Biomass Briquettes & Pellets?
          </h2>
          <p className="text-emerald-900 text-lg mb-8 tracking-wider">
            Contact us to discuss the right composting system for your requirements.
          </p>
          <Link to="/contact">
            <motion.button
              className="bg-emerald-900 hover:bg-emerald-800 text-white px-10 py-4 uppercase tracking-wider font-semibold inline-flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="composting-cta-btn"
            >
              Get in Touch
              <ArrowRight size={20} />
            </motion.button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Biomass;
